import { useEffect, useRef } from 'react';
import { getToken } from '../shared/api.ts';

export type DeviceEvent =
  | { type: 'quarantine.new'; item: unknown }
  | { type: 'quarantine.resolved'; id: string; action: 'kept' | 'discarded' }
  | { type: 'library.changed' }
  | { type: 'transfer.progress'; transferId: string; uploadId: string; bytes: number; total: number }
  | { type: 'session.claimed'; code: string }
  | { type: 'hello'; pendingCount: number };

/**
 * Live feed from the server, with exponential backoff. Browsers cannot set an
 * Authorization header on a WebSocket handshake, so the token rides in the
 * query string — the connection is TLS and the URL never leaves this client.
 */
export function useSocket(enabled: boolean, onEvent: (event: DeviceEvent) => void): void {
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    const token = getToken();
    if (!token) return;

    let socket: WebSocket | null = null;
    let retry = 0;
    let closed = false;
    let timer: number | undefined;

    const connect = (): void => {
      if (closed) return;
      const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
      socket = new WebSocket(`${scheme}://${location.host}/api/ws?token=${encodeURIComponent(token)}`);

      socket.onopen = () => {
        retry = 0;
      };
      socket.onmessage = (event) => {
        if (typeof event.data !== 'string' || event.data === 'pong') return;
        try {
          handler.current(JSON.parse(event.data) as DeviceEvent);
        } catch {
          // ignore malformed frames
        }
      };
      socket.onclose = () => {
        if (closed) return;
        const delay = Math.min(30_000, 500 * 2 ** retry);
        retry += 1;
        timer = window.setTimeout(connect, delay);
      };
      socket.onerror = () => socket?.close();
    };

    connect();

    const wake = (): void => {
      if (document.visibilityState === 'visible' && socket?.readyState !== WebSocket.OPEN) {
        window.clearTimeout(timer);
        retry = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', wake);

    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', wake);
      window.clearTimeout(timer);
      socket?.close();
    };
  }, [enabled]);
}
